import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { AI_API_URL, API_URL, API_WEBCAST, WEBCAST_TOKEN } from '@/config/environment';
import { logout } from '@/utils/logout';

import { handleTwoFactorRedirect } from './handleTwoFactorRedirect';
import { setupInterceptors } from './interceptors';

export const apiV1 = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    Accept: `application/json`,
  },
});

export const apiManagerIrmV1 = axios.create({
  baseURL: `${API_URL}/v1/irm`,
  headers: {
    Accept: `application/json`,
  },
});

export const apiAI = axios.create({
  baseURL: `${AI_API_URL}`,
  headers: {
    Accept: `application/json`,
  },
});

const requestInterceptor = (request: AxiosRequestConfig<unknown>) => {
  const storageType = localStorage.getItem(`storage-type`);
  const layoutView = localStorage.getItem(`layout-view`);
  const companyId = layoutView ? JSON.parse(layoutView)?.state?.company : null;
  let token = null;

  if (storageType === `localStorage`) {
    token = localStorage.getItem(`token`);
  } else if (storageType === `sessionStorage`) {
    token = sessionStorage.getItem(`token`);
  }

  if (request.headers) {
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }

    if (companyId) {
      request.headers[`x-company-id`] = companyId;
    }
  }

  return request;
};

const responseInterceptor = {
  fullfilled: (response: AxiosResponse<unknown, unknown>) => response,
  rejected: (error: { response: { status: number } }) => {
    if (handleTwoFactorRedirect(error)) {
      return Promise.reject(error);
    }

    if (error.response.status === 401) {
      logout();
      window.location.replace(`/login`);
    }

    return Promise.reject(error);
  },
};

apiV1.interceptors.request.use(requestInterceptor);
apiV1.interceptors.response.use(responseInterceptor.fullfilled, responseInterceptor.rejected);

apiManagerIrmV1.interceptors.request.use(requestInterceptor);
apiManagerIrmV1.interceptors.response.use(
  responseInterceptor.fullfilled,
  responseInterceptor.rejected,
);

apiAI.interceptors.request.use(requestInterceptor);
apiAI.interceptors.response.use(responseInterceptor.fullfilled, responseInterceptor.rejected);

setupInterceptors(apiV1);
setupInterceptors(apiManagerIrmV1);
setupInterceptors(apiAI);

export const apiWebcast = axios.create({
  baseURL: `${API_WEBCAST}/api`,
  headers: {
    Accept: `application/json`,
  },
});

apiWebcast.interceptors.request.use((request) => {
  const storageType = localStorage.getItem(`storage-type`);
  let token: string | null = null;

  if (storageType === `localStorage`) {
    token = localStorage.getItem(`token`);
  } else if (storageType === `sessionStorage`) {
    token = sessionStorage.getItem(`token`);
  }

  if (request.headers) {
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }
    request.headers[`x-auth-token`] = WEBCAST_TOKEN;
  }

  return request;
});

setupInterceptors(apiWebcast);
